import dayjs, { ConfigType, Dayjs, OpUnitType, QUnitType } from 'dayjs';
import { DurationUnitType } from '~/types/enums.types';

import { pluralize } from '~/utils/text.utils';
import { getUserTimezone, TFullTimezone } from '~/utils/timezone.utils';
import { REGEXP_24_HOUR_TIME_STRING } from '~/utils/regex';
import {
    formatDate as commonFormatDate,
    TDate,
    TSupportedDateFormat,
    formatDateWithLeadingZero as commonFormatDateWithLeadingZero,
    getAbsoluteDate as commonGetAbsoluteDate
} from '~/packages/utils';
import { ddRumLogger } from '~/services/logger/error.utils';

export const SECOND_IN_MS = 1000;
export const MINUTE_IN_MS = 60 * SECOND_IN_MS;
export const HOUR_IN_MS = 60 * MINUTE_IN_MS;

export const SHORT_DISPLAY_DATE_FORMAT = 'MM/DD/YYYY';

export const MONTHS_PER_YEAR = 12;

export const FIRST_MONTH_OF_YEAR_INDEX = 0;

export const FIRST_DAY_OF_MONTH_INDEX = 1;

export const LONG_MONTH_NAMES_FORMAT = 'MMMM';

export const SHORT_MONTH_NAMES_FORMAT = 'MMM';

export const DATEPICKER_FORMAT = 'MMM d, yyyy';

// These are the the only formats we should use
// Could be replaced by dayjs's llll format but requires another plugin: https://day.js.org/docs/en/display/format#localized-formats
export const ALLOWED_DATE_FORMATS_ALL_OTHER_FORMATS_ARE_DEPRECATED = {
    FULL_DATE: 'ddd, MMM D, YYYY',
    FULL_DATE_WITH_TIME: 'ddd, MMM D, YYYY [at] h:mmA'
} as const;

export const timeOptions = getTimeOptionsForTimepickerDropdown();

function _getTimeUnitsFromMS(milliseconds: number) {
    function timeUnit(divider) {
        return {
            exact: milliseconds / divider,
            get roundedDown() {
                return Math.floor(this.exact);
            },
            get roundedUp() {
                return Math.ceil(this.exact);
            }
        };
    }

    return {
        bySeconds: timeUnit(1000),
        byMinute: timeUnit(60000),
        byHour: timeUnit(3600000),
        byDay: timeUnit(86400000),
        byWeek: timeUnit(604800000),
        byMonth: timeUnit(2629800000)
    };
}

export const DAY_NAMES = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
];

export const SHORT_DAY_NAMES = [
    'Sun',
    'Mon',
    'Tue',
    'Wed',
    'Thu',
    'Fri',
    'Sat'
];

export const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
];

export const SHORT_MONTH_NAMES = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
];

export enum RelativeDays {
    TODAY = 'today',
    TOMORROW = 'tomorrow',
    NEXT_MONDAY = 'next-monday',
    YESTERDAY = 'yesterday'
}

export enum HoursInDay {
    MORNING = 'morning',
    NOON = 'noon',
    EVENING = 'evening'
}

export {
    type TDate,
    tryToFixDate,
    type TSupportedDateFormat
} from '~/packages/utils/date.utils';

export function formatDate(
    date: TDate,
    format: TSupportedDateFormat,
    is24HoursClock: boolean = false,
    timezone: string = undefined
) {
    return commonFormatDate(
        date,
        format,
        is24HoursClock,
        timezone,
        ddRumLogger
    );
}

export function formatDateWithLeadingZero(date: TDate) {
    return commonFormatDateWithLeadingZero(date, ddRumLogger);
}

export function buildDateTimeStringsObjectForDate(
    date: Dayjs,
    dateFormat = SHORT_DISPLAY_DATE_FORMAT
) {
    const timeNameIndex = date.hours() * 4 + Math.floor(date.minutes() / 15);

    return {
        date: date.format(dateFormat),
        time: timeOptions[timeNameIndex].value
    };
}

export function timeUntil(date: string | number | Date) {
    const now = Date.now();
    let targetDate;
    if (date instanceof Date) {
        targetDate = date.getTime();
    }
    targetDate = typeof date === 'string' ? Date.parse(date) : date;

    const difference = targetDate - now;
    if (difference < 0) {
        return -1;
    }

    const { byMinute, byHour, byDay, byWeek, byMonth } =
        _getTimeUnitsFromMS(difference);

    if (byMinute.exact < 2) {
        return 'a minute';
    }
    if (byHour.exact < 1) {
        return `${byMinute.roundedDown} ${pluralize(
            'minute',
            byMinute.roundedDown
        )}`;
    }

    if (byDay.exact < 1) {
        return `${byHour.roundedDown} ${pluralize('hour', byHour.roundedDown)}`;
    }
    if (byWeek.exact < 1) {
        return `${byDay.roundedUp} ${pluralize('day', byDay.roundedUp)}`;
    }
    if (byMonth.exact < 1) {
        return `${byWeek.roundedUp} ${pluralize('week', byWeek.roundedUp)}`;
    }
    return `${byMonth.roundedUp} ${pluralize('month', byMonth.roundedUp)}`;
}

export function timeSince(
    date: string | number | Date,
    dateFormat: TSupportedDateFormat = 'MMMM D, YYYY',
    prefix: string = 'on ',
    momentsAgoText = 'moments ago'
) {
    if (!date) {
        return null;
    }

    const now = Date.now();
    let targetDate: number;

    if (typeof date === 'number') {
        targetDate = date;
    } else if (typeof date === 'string') {
        targetDate = Date.parse(date);
    } else {
        targetDate = date.getTime();
    }

    const difference = now - targetDate;
    const { byMinute, byHour, byDay, byMonth } =
        _getTimeUnitsFromMS(difference);

    if (byMinute.exact < 1) {
        return momentsAgoText;
    }

    if (byHour.exact < 1) {
        return `${byMinute.roundedDown}m ago`;
    }

    if (byDay.exact < 1) {
        return `${byHour.roundedDown}h ago`;
    }

    if (byMonth.exact < 1 && byDay.exact < 8) {
        return `${byDay.roundedDown}d ago`;
    }

    return `${prefix}${formatDate(new Date(date), dateFormat)}`;
}

export function daysDifference(date1: TDate, date2: TDate) {
    return datesDifference(date1, date2, 'day');
}

export function datesDifference(
    date1: TDate,
    date2: TDate,
    unit: QUnitType | OpUnitType
) {
    const d1 = dayjs(formatDate(date1, 'MMMM D, YYYY'));
    const d2 = formatDate(date2, 'MMMM D, YYYY');
    return d1.diff(d2, unit);
}

export function isDateAfter(date1: TDate, date2: TDate) {
    return dayjs(date1).isAfter(date2);
}

export function isDateBefore(date1: TDate, date2: TDate) {
    return dayjs(date1).isBefore(date2);
}

export function isSameDate(date1: TDate, date2: TDate, unit?: OpUnitType) {
    return dayjs(date1).isSame(date2, unit);
}

export function isSameDay(date1: TDate, date2: TDate) {
    return isSameDate(date1, date2, 'day');
}

export type TDayJSTimeUnits = 'hour' | 'day' | 'week' | 'month';

export function getFutureDate(
    givenDate: TDate,
    unit: TDayJSTimeUnits,
    unitCount: number
) {
    const date = dayjs.utc(givenDate);

    if (!date.isValid()) {
        throw new Error('Parameter provided is not valid date constructor');
    }

    return date.add(unitCount, unit).toISOString();
}

export function getAbsoluteDate(date: TDate, format?: TSupportedDateFormat) {
    return commonGetAbsoluteDate(date, ddRumLogger, format);
}

export function getPastDate(
    givenDate: TDate,
    unit: TDayJSTimeUnits,
    unitCount: number
) {
    const date = dayjs(givenDate);

    if (!date.isValid()) {
        throw new Error('Parameter provided is not valid date constructor');
    }

    return date.subtract(unitCount, unit).toISOString();
}

export function getInitialTime(
    date: Date = new Date()
): (RelativeDays | HoursInDay | string)[] {
    const day = date.getDay();
    const hour = date.getHours();

    if (day >= 1 && day <= 5) {
        if (hour >= 0 && hour < 8) {
            return [RelativeDays.TODAY, HoursInDay.MORNING];
        }
        if (hour >= 8 && hour < 11) {
            return [RelativeDays.TODAY, HoursInDay.NOON];
        }
        if (hour >= 11 && hour < 17) {
            return [RelativeDays.TODAY, HoursInDay.EVENING];
        }
    }

    if ((day === 5 && hour >= 17) || day === 6) {
        return [RelativeDays.NEXT_MONDAY, HoursInDay.MORNING];
    }

    return [RelativeDays.TOMORROW, HoursInDay.MORNING];
}

export function getLongTimeZone(date: Date = new Date()) {
    return date
        .toLocaleDateString('en-US', {
            day: '2-digit',
            timeZoneName: 'long'
        })
        .substring(4);
}

export function getShortTimeZone(lTimeZone: string) {
    return lTimeZone.match(/\b(\w)/g)
        ? lTimeZone.match(/\b(\w)/g).join('')
        : '';
}

export function getActivityWindowOfHoursInADay() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    const endDate = new Date();
    endDate.setHours(23, 30, 0, 0);

    const activityHours = [];

    while (endDate >= date) {
        activityHours.push(new Date(date));
        date.setTime(date.getTime() + 1000 * 60 * 30);
    }

    return activityHours;
}

// Use 0 for January, 1 for February, etc.
export function daysInMonth(month, year = new Date().getFullYear()) {
    return new Date(year, month + 1, 0).getDate();
}

export function isFutureDate(date: ConfigType) {
    return dayjs(date).isAfter(dayjs());
}

/**use to get is Future, when you have date without time, and time in h:mm a format (9:00 am / 10:00 am) */
export function isFutureDateAndTime(date: Date, time?: string) {
    const targetDate = dayjs(date);
    const now = dayjs();

    if (!time || !targetDate.isSame(now, 'day')) {
        return targetDate.isAfter(now, 'day');
    }
    const targetTime = convert12HourTimeStringTo24HoursTimeString(time);
    return timeToMinutes(targetTime) > timeToMinutes(now.format('HH:mm'));
}

export const formatRelativeDate = ({
    date,
    withoutYear = false,
    timezone,
    dateFormat
}: {
    date: Date | string;
    withoutYear?: boolean;
    timezone?: TFullTimezone;
    dateFormat?: string;
}) => {
    if (date) {
        const now = timezone ? dayjs().tz(timezone) : dayjs();
        const targetDate = dayjs(date);

        if (targetDate.isSame(now, 'day')) {
            return RelativeDays.TODAY;
        }
        if (targetDate.isSame(now.add(1, 'day'), 'day')) {
            return RelativeDays.TOMORROW;
        }
        if (targetDate.isSame(now.subtract(1, 'day'), 'day')) {
            return RelativeDays.YESTERDAY;
        }
        const format =
            dateFormat || (withoutYear ? 'ddd, MMM D' : 'ddd, MMM D, YYYY');
        return targetDate.format(format);
    }

    return '';
};

// See https://stackoverflow.com/a/27768777
// including https://stackoverflow.com/questions/17545708/parse-date-without-timezone-javascript#comment64589552_27768777
export function dateStringToDateIgnoreTimezone(dateString: string) {
    if (!dateString) {
        return null;
    }

    if (dateString.indexOf('T') === -1) {
        return new Date(dateString);
    }

    const fixedDateArr = dateString.substring(0, 10).split('-');
    const fixedDate =
        fixedDateArr[1] + '/' + fixedDateArr[2] + '/' + fixedDateArr[0];

    return new Date(fixedDate);
}

export const padTo2Digits = num => {
    return num.toString().padStart(2, '0');
};

export function getTimeOptionsForTimepickerDropdown({
    isValueIn24hFormat
}: {
    isValueIn24hFormat?: boolean;
} = {}) {
    const result: Array<{ value: string; label: string }> = [];
    for (let timePart = 0; timePart < 24 * 4; timePart++) {
        let hours = Math.floor(timePart / 4) % 12;
        hours = hours === 0 ? 12 : hours;
        const minutes = ((timePart * 15) % 60).toString().padEnd(2, '0');
        const timeOfDay = timePart / 24 / 2 < 1 ? 'am' : 'pm';

        const resultTime = `${hours}:${minutes} ${timeOfDay}`;
        const value = isValueIn24hFormat
            ? convert12HourTimeStringTo24HoursTimeString(resultTime)
            : resultTime;
        result.push({ value, label: resultTime });
    }

    return result;
}

export function convertDateAndTimeToDateStringsWithTimezone(
    inputDate: Date,
    time: string,
    timezone: string | undefined
) {
    let originalResults = convertDateAndTimeToDateStrings(inputDate, time);
    if (!timezone) {
        return originalResults;
    }
    // add the offset to the local time
    let localTimezone = getUserTimezone();
    // find the offset difference
    let localOffset = dayjs(inputDate).tz(localTimezone).utcOffset();
    let targetOffset = dayjs(inputDate).tz(timezone).utcOffset();
    let offsetDifference = (targetOffset - localOffset) / 60;
    let adjustedLocalDate = dayjs(originalResults.local)
        .subtract(offsetDifference, 'hour')
        .toDate();
    return generateLocalAndUTCDateTimeStrings(adjustedLocalDate);
}

export function convertDateAndTimeToDateStrings(inputDate: Date, time: string) {
    // Extract hour and minute from the time string
    const [hourMinute, meridiem] = time.split(' ');
    let [hours, minutes] = hourMinute.split(':').map(Number);

    // Convert 12-hour format to 24-hour format
    if (meridiem.toLowerCase() === 'pm' && hours !== 12) {
        hours += 12;
    } else if (meridiem.toLowerCase() === 'am' && hours === 12) {
        hours = 0;
    }

    // Create a new Date object for the input date and set local time
    const localDateTime = new Date(inputDate);
    localDateTime.setHours(hours, minutes, 0, 0);

    return generateLocalAndUTCDateTimeStrings(localDateTime);
}

function generateLocalAndUTCDateTimeStrings(date: Date) {
    // Generate local and UTC date-time strings
    const localString = formatDateTimeString(date, false);
    const utcString = formatDateTimeString(date, true);

    // Format and return the UTC date and time
    return {
        utc: utcString,
        local: localString
    };
}

export function formatDateTimeString(date: Date, isUTC: boolean): string {
    const year = isUTC ? date.getUTCFullYear() : date.getFullYear();
    const month = String(
        (isUTC ? date.getUTCMonth() : date.getMonth()) + 1
    ).padStart(2, '0');
    const day = String(isUTC ? date.getUTCDate() : date.getDate()).padStart(
        2,
        '0'
    );

    const hours = String(isUTC ? date.getUTCHours() : date.getHours()).padStart(
        2,
        '0'
    );
    const minutes = String(
        isUTC ? date.getUTCMinutes() : date.getMinutes()
    ).padStart(2, '0');
    const seconds = String(
        isUTC ? date.getUTCSeconds() : date.getSeconds()
    ).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${
        isUTC ? '.000' : ''
    }`;
}

export function getTimeOptionsStringFromDate(dateTime: Date) {
    // returns the date, and the time in the format of hh:mm AM/PM
    const date = new Date(dateTime);
    const hour = date.getHours();
    const minute = date.getMinutes();
    const period = hour >= 12 ? 'pm' : 'am';

    const hour12 = hour % 12 || 12;
    const minuteStr = minute.toString().padStart(2, '0');

    return `${hour12}:${minuteStr} ${period}`;
}

export function timeToMinutes(time: string): number {
    const [hourPart, minutePart, amPm] = time.split(/[:\s]/);
    let hour = parseInt(hourPart, 10);
    const minute = parseInt(minutePart, 10);

    if (amPm === 'pm' && hour !== 12) {
        hour += 12;
    }
    if (amPm === 'am' && hour === 12) {
        hour = 0;
    }

    return hour * 60 + minute;
}

type Offets = 0 | 15 | 30 | 45 | 60;

export function getClosestFutureTimeForTimepickerDropdown(
    offsetMinutes: Offets = 0,
    currentDate: Date = new Date()
): string {
    const currentMinutes =
        (currentDate.getHours() * 60 +
            currentDate.getMinutes() +
            offsetMinutes) %
        1440; // offset to next day if needed (1440 minutes in a day);

    for (const option of timeOptions) {
        const optionMinutes = timeToMinutes(option.value);
        if (optionMinutes >= currentMinutes) {
            // Notice the change here to ">="
            return option.value;
        }
    }

    // If no future time is found, return the first time of the next day.
    return timeOptions[0].value;
}

export function getNextFullHour({
    date = dayjs(),
    timezone = 'America/Los_Angeles'
}: {
    date?: Dayjs;
    timezone?: TFullTimezone;
}) {
    return dayjs(date)
        .tz(timezone)
        .add(1, 'hour')
        .startOf('hour')
        .format('HH:00');
}

export function isTimeIn12HourFormat(time: string) {
    const time12HourRegex = /^(1[0-2]|0?[1-9]):([0-5][0-9]) ?([APap][Mm])$/;
    return time12HourRegex.test(time);
}

export function hasTimePassed({
    dueDate,
    dueTime,
    timezone = 'America/Los_Angeles'
}: {
    dueDate: Date;
    dueTime?: string;
    timezone?: TFullTimezone;
}) {
    const dueDateDayjs = dayjs.tz(
        dayjs(dueDate).format('YYYY-MM-DD'),
        timezone
    );

    const timeToCompareTo =
        !!dueTime && isTimeIn12HourFormat(dueTime)
            ? convert12HourTimeStringTo24HoursTimeString(dueTime)
            : dueTime;

    const [hours, minutes] = timeToCompareTo
        ? timeToCompareTo.split(':').map(Number)
        : [23, 59];

    const dueDateTime = dueDateDayjs.hour(hours).minute(minutes);

    const now = dayjs().tz(timezone);

    return dueDateTime.isBefore(now);
}

export function getEndDateFromStartDateAndDuration(
    startDate: Date,
    startTime: string, // hh:mm AM/PM,
    duration: {
        unit_count: number;
        unit_type: DurationUnitType;
    }
) {
    // Extract hours, minutes, and am/pm from the start time
    const [, hourStr, minuteStr, period] =
        startTime.match(/(\d+):(\d+) (am|pm)/i) || [];

    let hours = parseInt(hourStr, 10);
    const minutes = parseInt(minuteStr, 10);

    // Adjust for 12-hour format
    if (period.toLowerCase() === 'pm' && hours !== 12) {
        hours += 12;
    }
    if (period.toLowerCase() === 'am' && hours === 12) {
        hours -= 12;
    }

    // Set hours and minutes to the start date
    startDate.setHours(hours, minutes);

    switch (duration.unit_type) {
        case 'seconds':
            startDate.setSeconds(startDate.getSeconds() + duration.unit_count);
            break;
        case 'minutes':
            startDate.setMinutes(startDate.getMinutes() + duration.unit_count);
            break;
        case 'hours':
            startDate.setHours(startDate.getHours() + duration.unit_count);
            break;
        case 'days':
            startDate.setDate(startDate.getDate() + duration.unit_count);
            break;
        case 'weeks':
            startDate.setDate(startDate.getDate() + 7 * duration.unit_count);
            break;
        case 'months':
            startDate.setMonth(startDate.getMonth() + duration.unit_count);
            break;
        case 'years':
            startDate.setFullYear(
                startDate.getFullYear() + duration.unit_count
            );
            break;
        default:
            throw new Error(`Unsupported unit type: ${duration.unit_type}`);
    }

    return startDate;
}

// Temp
export function formatDateRange(
    dates: Array<string | undefined | null>,
    fallback = 'TBD'
) {
    // based on https://github.com/HoneyBook/HoneybookPaymentsDev/blob/main/app/assets/javascripts/angular/app/modules/common/filters/hbdate_filter.js

    const _formatDate = (date, format) => dayjs(date).format(format);

    const partialDate = 'ddd, MMM D';
    const fullDate = 'ddd, MMM D, YYYY';

    if (!dates[0]) {
        return fallback;
    }

    if (dates.length !== 2 || !dates[1]) {
        return _formatDate(dates[0], fullDate);
    }

    if (dates[0] === dates[1]) {
        return _formatDate(dates[0], fullDate);
    }

    return (
        _formatDate(dates[0], partialDate) +
        ' - ' +
        _formatDate(dates[1], fullDate)
    );
}

export const convertMillisecondsToTime = (milliseconds: number) => {
    let seconds = Math.floor(milliseconds / 1000);
    let minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    seconds = seconds % 60;
    minutes = minutes % 60;

    return {
        hours,
        minutes,
        seconds
    };
};

export function padTimeWithZero(time: string) {
    const [hours, minutes] = time.split(':');
    return `${padTo2Digits(Number(hours))}:${padTo2Digits(Number(minutes))}`;
}

export function getDatesBetween(startDate: TDate, endDate: TDate) {
    const startRange = dayjs(startDate).isBefore(endDate) ? startDate : endDate;
    const endRange = dayjs(startDate).isBefore(endDate) ? endDate : startDate;
    const ranges: Dayjs[] = [];
    const diffDays = dayjs(endRange).diff(dayjs(startRange), 'day');

    for (let i = 0; i <= diffDays; i++) {
        ranges.push(dayjs(startRange).add(i, 'day'));
    }

    return ranges;
}

export const convert12HourTimeStringTo24HoursTimeString = time => {
    const [_hours, _minutesPart] = time.split(':');
    if (!_minutesPart) {
        throw new Error('Invalid time format');
    }

    const minutePartMatch = _minutesPart.match(/(\d{2})(?:\s*([apAP][mM]))?$/);

    if (!minutePartMatch) {
        throw new Error('Invalid time format');
    }

    const [, _minutes, period] = minutePartMatch;

    if (!_minutes || !period) {
        throw new Error('Invalid time format');
    }

    let hours = parseInt(_hours, 10);
    let minutes = parseInt(_minutes, 10);

    if (
        isNaN(hours) ||
        isNaN(minutes) ||
        hours < 1 ||
        hours > 12 ||
        minutes < 0 ||
        minutes > 59
    ) {
        throw new Error('Invalid time values');
    }

    if (period.toLowerCase() !== 'am' && period.toLowerCase() !== 'pm') {
        throw new Error('Invalid period');
    }

    if (period.toLowerCase() === 'pm' && hours !== 12) {
        hours += 12;
    } else if (period.toLowerCase() === 'am' && hours === 12) {
        hours = 0;
    }

    return `${padTo2Digits(hours)}:${padTo2Digits(minutes)}`;
};

export const convert24HourTimeStringTo12HourString = (time: string) => {
    const [_hours, _minutes] = time.split(':');
    if (!_hours || !_minutes) {
        throw new Error('Invalid time format');
    }

    const hours = parseInt(_hours, 10);
    const minutes = parseInt(_minutes, 10);

    if (hours === 24 && minutes === 0) {
        return `12:00 am`;
    }

    if (
        isNaN(hours) ||
        isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
    ) {
        throw new Error('Invalid time values');
    }

    const period = hours >= 12 ? 'pm' : 'am';
    const hour12 = hours % 12 || 12;

    return `${hour12}:${padTo2Digits(minutes)} ${period}`;
};

export const getCurrentYear = () => {
    return new Date().getFullYear();
};

export const getMidnightUtcDate = (date: TDate) => {
    return dayjs(date).utcOffset(0).startOf('date').toDate();
};

/**
 * Method that validates and converts a time string input by the user to the 24h time string
 * @param timeValue a time string in either 12h or 24h format that was input by the user
 * @returns either converted string to 24h format, or throws exception if string is invalid
 */
export const getConvertedTimeString = (timeValue: string): string => {
    const is24HourFormat = REGEXP_24_HOUR_TIME_STRING.test(timeValue);

    if (is24HourFormat) {
        return timeValue;
    }

    return convert12HourTimeStringTo24HoursTimeString(timeValue);
};

const REGEXP_FLEXIBLE_12_HOUR_TIME_STRING =
    /^(0?[1-9]|1[0-2])(?::?([0-5][0-9]))?(?:\s*(AM|PM))?$/i;

const REGEXP_FLEXIBLE_24_HOUR_TIME_STRING =
    /^(0|[01][0-9]|2[0-3])(?::?([0-5][0-9]))?$|^24(:?00)?$/i;

export const convertAnyTimeStringTo12HourFormat = timeString => {
    if (!timeString?.match) {
        return null;
    }

    const match12HrTime = timeString.match(REGEXP_FLEXIBLE_12_HOUR_TIME_STRING);
    if (match12HrTime) {
        const [, hour, minute = '00', period = 'am'] = match12HrTime;
        return `${parseInt(hour)}:${minute} ${period.toLowerCase()}`;
    }

    const match24HrTime = timeString.match(REGEXP_FLEXIBLE_24_HOUR_TIME_STRING);
    if (match24HrTime) {
        const [time, hour, minute = '00'] = match24HrTime;

        if (time === '24' || time === '24:00' || time === '2400') {
            return '12:00 am';
        }

        const period = hour >= 12 ? 'pm' : 'am';

        return `${hour % 12 || 12}:${minute} ${period}`;
    }

    return null;
};

export const autoCompleteTime = (inputValue: string): string => {
    const match = inputValue.match(/^(\d{1,2})(\d{2})?(?:[:\s]*)?(am|pm)?$/i);
    if (match) {
        let [, hours, minutes = '', period] = match;
        minutes = minutes ? minutes.padEnd(2, '0') : '00';

        const formattedTime = `${parseInt(hours, 10)}:${minutes}`;

        let res = period
            ? `${formattedTime} ${period.toLowerCase()}`
            : formattedTime;
        return res.trim();
    }
    return inputValue.trim();
};

export const isValidTimeString = (timeString: string): boolean => {
    return (
        REGEXP_FLEXIBLE_12_HOUR_TIME_STRING.test(timeString) ||
        REGEXP_FLEXIBLE_24_HOUR_TIME_STRING.test(timeString)
    );
};

/**
 * Adds a specified number of milliseconds to a given time string in the format `h:mm a`
 * (e.g., `"2:30 pm"`, `"10:45 am"`) and returns the updated time in the same format.
 *
 * @param {string} params.time - The base time in 12-hour format with "am" or "pm".
 * @param {number} params.ms - The number of milliseconds to add.
 * @returns {string} - The updated time string in the format `h:mm a`.
 */
export function addMillisecondsToTime({
    time,
    ms
}: {
    time: string;
    ms: number;
}): string {
    // NOTE: do not use `dayjs(SOME_DATE_IN_AM_PM_FORMAT, "YYYY-MM-DD h:mm a");` since it will result in InvalidDate in Safari

    const today = dayjs().format('YYYY-MM-DD');

    // Use regex to properly extract hours, minutes, and period (AM/PM)
    const match = time.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)?$/i);
    if (!match) {
        throw new Error(`Invalid time format: ${time}`);
    }

    let hours = Number(match[1]);
    const minutes = match[2];
    const period = match[3]?.toUpperCase(); // Ensure PM/AM is uppercase

    if (period === 'PM' && hours !== 12) {
        hours += 12;
    } else if (period === 'AM' && hours === 12) {
        hours = 0;
    }

    // Construct a valid ISO-like string
    const fullDateTime = `${today}T${String(hours).padStart(
        2,
        '0'
    )}:${minutes}`;

    const parsedTime = dayjs(fullDateTime);

    return parsedTime.add(ms, 'milliseconds').format('h:mm a');
}

/**
 * Computes the time elapsed since the start of the day and the time remaining until the end of the day,
 * based on a given time string in the `h:mm a` format (e.g., `"2:30 pm"`, `"10:45 am"`).
 *
 * @param {string} time - A string representing the time in 12-hour format with "am" or "pm".
 * @returns {{
 *    timePassedSinceStartOfDay: number;
 *    timeRemainingUntilEndOfDay: number;
 * }} - An object containing:
 *   - `timePassedSinceStartOfDay`: The number of milliseconds elapsed since midnight.
 *   - `timeRemainingUntilEndOfDay`: The number of milliseconds remaining until midnight.
 */
export function getTimeElapsedAndRemainingInDay(time: string) {
    const now = dayjs();

    const [timePart, period] = time.split(' ');
    const [hourStr, minuteStr] = timePart.split(':');

    let hours = Number(hourStr);
    const minutes = Number(minuteStr);

    // Convert 12-hour format to 24-hour format
    if (period.toLowerCase() === 'pm' && hours !== 12) {
        hours += 12;
    } else if (period.toLowerCase() === 'am' && hours === 12) {
        hours = 0;
    }

    const dateTime = now.hour(hours).minute(minutes).second(0).millisecond(0);

    const startOfDay = now.startOf('day');
    const timePassedSinceStartOfDay = dateTime.diff(startOfDay, 'milliseconds');

    const endOfDay = now.endOf('day');
    const timeRemainingUntilEndOfDay = endOfDay.diff(dateTime, 'milliseconds');

    return {
        timePassedSinceStartOfDay,
        timeRemainingUntilEndOfDay
    };
}

export const isMidnight = (date: TDate) => {
    const dayjsDate = dayjs(date);

    return (
        dayjsDate.hours() === 0 &&
        dayjsDate.minutes() === 0 &&
        dayjsDate.seconds() === 0 &&
        dayjsDate.milliseconds() === 0
    );
};
